"use client";

import { useEffect, useRef } from "react";
import { canScheduleTruckloads } from "@shared/products/checkout";
import { PaymentChoice } from "@/components/cart/payment-choice";
import Link from "next/link";
import { CHECK_AVAILABILITY_LABEL } from "@shared/products/listing-copy";
import { AddToCartButton } from "@/components/cart/add-to-cart-button";
import { BuyNowButton } from "@/components/cart/buy-now-button";
import { CheckoutSummaryRows } from "@/components/cart/checkout-summary-rows";
import { CheckoutRedirectOverlay } from "@/components/cart/checkout-redirect-overlay";
import { GuestCheckoutContactDialog } from "@/components/cart/guest-checkout-contact-dialog";
import type { GuestCheckoutContact } from "@/hooks/checkout/use-guest-contact-prompt";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics/tracker";
import { buildGA4Item } from "@/lib/analytics/ga4/items";
import { trackGA4AddToCart } from "@/lib/analytics/ga4/ecommerce";
import { ListingBookingBlock } from "./listing-booking-block";
import { useListingBuyState } from "./listing-buy-state";
import { buildListingQuoteHref } from "./listing-quote-href";
import { ListingBuyAgain } from "./listing-buy-again";
import { ListingAddressDialog } from "./listing-address-dialog";
import { usePublishListingQuote } from "./listing-quote-summary";
import {
  cancelListingCheckout,
  pendingListingCheckout,
  takeListingCheckoutRequest,
  useListingCheckoutRequest,
} from "@/lib/chat/listing-checkout-request";
import { formatCents } from "@/lib/formatting/currency";
import { formatNumber } from "@/lib/formatting/numbers";
import { productUnitNouns } from "@/lib/products/unit-nouns";
import {
  isResolvingDelivery,
  useListingPurchase,
  type ListingBuyState,
  type ListingProduct,
  type ListingPurchase,
} from "./use-listing-purchase";

export function ListingActionEnhancements({
  unitName,
  unitNamePlural,
  ...product
}: ListingProduct & { unitName: string; unitNamePlural: string }) {
  const state = useListingBuyState();
  useChatCheckoutQuantity(state);
  const needsQuote = !canScheduleTruckloads(
    state.checkoutMinimum,
    product.truckOptions ?? product.truckCapacity,
    state.quantity,
  );

  return (
    <div className="space-y-4">
      <ListingBookingBlock
        unitName={unitName}
        unitNamePlural={unitNamePlural}
      />
      {needsQuote ? (
        <>
          <ListingQuoteAction
            slug={product.productSlug ?? product.productPublicIdentifier}
            state={state}
          />
          <ListingAddressDialog />
        </>
      ) : (
        <ListingPurchaseActions product={product} state={state} />
      )}
      <ListingBuyAgain productId={product.productId} />
    </div>
  );
}

function ListingQuoteAction({
  slug,
  state,
}: {
  slug?: string;
  state: ListingBuyState;
}) {
  if (!slug || isResolvingDelivery(state)) {
    return (
      <Button variant="primary" disabled className="w-full">
        {CHECK_AVAILABILITY_LABEL}
      </Button>
    );
  }
  const href = buildListingQuoteHref({
    slug,
    quantity: state.quantity,
    preferredDate: state.deliveryWindow.start,
  });
  return (
    <Button asChild variant="primary" className="w-full">
      <Link href={href}>{CHECK_AVAILABILITY_LABEL}</Link>
    </Button>
  );
}

function ListingEstimate({
  purchase,
  goodsLabel,
}: {
  purchase: ListingPurchase;
  goodsLabel: string;
}) {
  return (
    <div className="border-t border-border pt-4">
      <CheckoutSummaryRows
        compact
        itemCount={1}
        goodsLabel={goodsLabel}
        goodsCents={purchase.goodsCents}
        totalCents={purchase.totalCents}
        freight={purchase.freight}
        complete={purchase.complete}
        loadCount={purchase.loadCount}
      />
      {purchase.freight.error ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={purchase.freight.refresh}
        >
          Retry delivery quote
        </Button>
      ) : null}
    </div>
  );
}

interface ListingActionProps {
  product: ListingProduct;
  state: ListingBuyState;
}

function listingGoodsLabel(product: ListingProduct, { quantity }: ListingBuyState) {
  const nouns = productUnitNouns(product.productTypeSlug);
  return `${formatNumber(quantity)} ${quantity === 1 ? nouns.unitName : nouns.unitNamePlural}`;
}

function isQuoted(purchase: ListingPurchase) {
  return (
    purchase.complete &&
    purchase.freight.quote !== null &&
    !purchase.freight.loading
  );
}

function isPurchaseBlocked(state: ListingBuyState, purchase: ListingPurchase) {
  return (
    isResolvingDelivery(state) ||
    purchase.isLoading ||
    purchase.freight.loading ||
    purchase.freight.error !== null ||
    (purchase.terms.onTerms && !purchase.terms.readyOnTerms)
  );
}

function scrollToActionCard() {
  document
    .getElementById("listing-action-card")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function isReadyForChatCheckout(
  state: ListingBuyState,
  purchase: ListingPurchase,
  hasAddress: boolean,
): boolean {
  return (
    !state.delivery.isLoading &&
    !isResolvingDelivery(state) &&
    !purchase.isLoading &&
    !purchase.terms.resolving &&
    !purchase.terms.onTerms &&
    (!hasAddress || isQuoted(purchase) || purchase.freight.error !== null)
  );
}

function useChatCheckoutQuantity({ quantity, setQuantity }: ListingBuyState) {
  const request = useListingCheckoutRequest();
  const appliedRef = useRef<number | null>(null);
  useEffect(() => {
    const pending = pendingListingCheckout(window.location.pathname);
    if (!pending || pending.quantity === quantity) return;
    if (appliedRef.current === pending.requestedAt) {
      cancelListingCheckout();
      return;
    }
    appliedRef.current = pending.requestedAt;
    setQuantity(pending.quantity);
  }, [request, quantity, setQuantity]);
}

function useChatCheckoutRequest(ready: boolean, quantity: number, buy: () => Promise<void>) {
  const request = useListingCheckoutRequest();
  useEffect(() => {
    if (ready && takeListingCheckoutRequest(window.location.pathname, quantity)) void buy();
  }, [request, ready, quantity, buy]);
}

function useListingBuyActions({ product, state }: ListingActionProps) {
  const purchase = useListingPurchase(product, state);
  const hasAddress = Boolean(state.delivery.location?.line1);
  const quoted = isQuoted(purchase);
  const blocked = isPurchaseBlocked(state, purchase);
  const hadAddress = useRef(hasAddress);
  useEffect(() => {
    if (hasAddress && !hadAddress.current) {
      track("checkout.address_added", {
        surface: "listing",
        productId: product.productId,
      });
    }
    hadAddress.current = hasAddress;
  }, [hasAddress, product.productId]);

  const startPurchase = async (contact?: GuestCheckoutContact) => {
    state.closeAddressDialog();
    if (await purchase.buyNow(contact)) {
      trackPurchaseStarted(product, state.quantity);
    }
  };
  const buy = async () => {
    trackBuyNow(product, state.quantity, hasAddress);
    if (!hasAddress) {
      state.openAddressDialog("buy");
      return;
    }
    if (blocked || !quoted) {
      scrollToActionCard();
      return;
    }
    await startPurchase();
  };

  useChatCheckoutRequest(isReadyForChatCheckout(state, purchase, hasAddress), state.quantity, buy);

  usePublishListingQuote({
    totalCents: quoted ? purchase.totalCents : null,
    goodsCents: purchase.goodsCents,
    quantity: state.quantity,
    actionLabel: purchase.terms.onTerms ? "Place order" : "Buy now",
    buy: () => void buy(),
  });

  return { purchase, hasAddress, quoted, blocked, buy, startPurchase };
}

function buyNowLabel(purchase: ListingPurchase, quoted: boolean) {
  const total = quoted ? ` · ${formatCents(purchase.totalCents)}` : "";
  if (purchase.terms.onTerms) {
    return `Place order on ${purchase.terms.termsLabel ?? "terms"}${total}`;
  }
  return `Buy now${total}`;
}

function addressCheckoutTerms(purchase: ListingPurchase) {
  const { onTerms, termsLabel } = purchase.terms;
  return {
    termsLabel: onTerms ? (termsLabel ?? "terms") : null,
    collectContact: purchase.needsContact && !onTerms,
  };
}

function purchaseReturnTo(productSlug: string | undefined) {
  return productSlug ? `/listing/${productSlug}` : "/cart";
}

function ListingPurchaseActions({ product, state }: ListingActionProps) {
  const { purchase, hasAddress, quoted, blocked, buy, startPurchase } =
    useListingBuyActions({ product, state });
  const returnTo = purchaseReturnTo(product.productSlug);
  const totalLabel = quoted ? formatCents(purchase.totalCents) : null;

  return (
    <>
      {purchase.isRedirecting && <CheckoutRedirectOverlay isRedirecting />}
      <ListingEstimate purchase={purchase} goodsLabel={listingGoodsLabel(product, state)} />
      <PaymentChoice payment={purchase.terms} returnTo={returnTo} />
      <div className="space-y-1 pt-1">
        <BuyNowButton
          label={buyNowLabel(purchase, quoted)}
          onBuy={buy}
          disabled={hasAddress && (blocked || !quoted)}
        />
        <AddToCartButton
          {...product}
          variant="ghost"
          quantity={state.quantity}
          unitPriceCents={product.priceCents}
          onAdded={() =>
            purchase.seedShipment({
              replace: hasAddress,
            })
          }
          disabled={isResolvingDelivery(state)}
        />
      </div>
      <ListingAddressDialog
        checkout={{
          ...addressCheckoutTerms(purchase),
          totalLabel:
            totalLabel ?? `${formatCents(purchase.goodsCents)} + delivery`,
          quoted,
          ready: hasAddress && quoted && !blocked,
          returnTo,
          onContinue: (contact) => void startPurchase(contact),
        }}
      />
      <GuestCheckoutContactDialog
        prompt={purchase.contactPrompt}
        totalLabel={totalLabel}
        returnTo={returnTo}
      />
    </>
  );
}

function trackBuyNow(
  product: ListingProduct,
  quantity: number,
  hasAddress: boolean,
) {
  track("checkout.buy_now_clicked", {
    productId: product.productId,
    quantity,
    hasAddress,
    productType: product.productTypeSlug,
    value: (product.priceCents * quantity) / 100,
    currency: "USD",
  });
}

function trackPurchaseStarted(product: ListingProduct, quantity: number) {
  trackGA4AddToCart(
    buildGA4Item({
      itemId: product.productPublicIdentifier ?? product.productId,
      itemName: product.productTitle,
      priceCents: product.priceCents,
      quantity,
      category: product.productTypeSlug,
    }),
    (product.priceCents * quantity) / 100,
  );
}
